import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SEO } from '@/lib/seo';
import { Reveal } from '@/lib/motion';
import { getCategories, getProducts } from '@/lib/api';
import { BUSINESS } from '@/data/site';
import { MONEY_PAGES } from '@/data/moneyPages';
import ProductCard from '@/components/ProductCard';
import { EcwidLink, ecwidCategoryUrl } from '@/components/EcwidWidgets';

export default function CategoryLanding({ page }) {
  const { data: categories } = useQuery({ queryKey: ['categories'], queryFn: getCategories, staleTime: 120000 });
  const { data: products, isLoading } = useQuery({
    queryKey: ['money-products', page.ecwidSlug],
    queryFn: () => getProducts({ category: page.ecwidSlug, per_page: 8 }),
    staleTime: 120000,
  });
  const liveCat = categories?.find((c) => c.slug === page.ecwidSlug);
  const fullMenuTo = liveCat ? ecwidCategoryUrl(liveCat) : '/shop';
  const related = MONEY_PAGES.filter((p) => p.slug !== page.slug);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: page.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://majesticgreensdmv.com/' },
        { '@type': 'ListItem', position: 2, name: 'Shop', item: 'https://majesticgreensdmv.com/shop' },
        { '@type': 'ListItem', position: 3, name: page.label, item: `https://majesticgreensdmv.com/${page.slug}` },
      ],
    },
  ];

  return (
    <>
      <SEO title={page.title} description={page.meta} path={`/${page.slug}`} jsonLd={jsonLd} />
      <div data-testid={`money-page-${page.slug}`} className="pb-28 pt-28">
        <div className="grain relative overflow-hidden border-b border-ink/10 bg-parchment">
          <div className="relative mx-auto max-w-4xl px-6 py-16">
            <Reveal>
              <p className="text-[11px] font-bold uppercase tracking-[0.4em] text-bronze">
                <Link to="/" className="hover:text-bronzehover">Home</Link>
                <span className="text-ash"> / </span>
                <Link to="/shop" data-testid="money-breadcrumb-shop" className="hover:text-bronzehover">Shop</Link>
                <span className="text-ash"> / {page.label}</span>
              </p>
              <h1 className="mt-4 font-serif text-4xl text-ink sm:text-6xl">{page.h1}</h1>
            </Reveal>
          </div>
        </div>

        <div className="mx-auto max-w-4xl px-6">
          {page.intro.map((para, i) => (
            <Reveal key={i} delay={0.05 * i}>
              <p className="mt-6 text-base leading-relaxed text-sage">{para}</p>
            </Reveal>
          ))}

          <Reveal delay={0.1}>
            <div className="mt-10 flex flex-wrap gap-2">
              {page.links.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`money-link-${l.to.replace(/[^a-z]+/g, '-')}`}
                  className="group flex items-center gap-2 border border-ink/10 px-4 py-2.5 text-xs font-semibold text-sage transition-colors hover:border-gold/50 hover:text-ink"
                >
                  {l.label} <ChevronRight className="h-3.5 w-3.5 text-ash transition-transform group-hover:translate-x-0.5" />
                </Link>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="mx-auto mt-16 max-w-7xl px-6">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <h2 className="text-3xl font-black tracking-[-0.02em] text-ink sm:text-4xl">Live from the menu</h2>
              <EcwidLink to={fullMenuTo} data-testid="money-full-menu-link" className="gold-underline flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-sage hover:text-forest">
                Full {page.label} selection <ArrowRight className="h-4 w-4" />
              </EcwidLink>
            </div>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-px border border-ink/10 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4">
            {isLoading
              ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="aspect-[3/4] animate-pulse bg-parchment" />)
              : (products || []).slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
          <Reveal delay={0.1}>
            <EcwidLink
              to={fullMenuTo}
              data-testid="money-shop-cta"
              className="group mt-10 inline-flex items-center gap-3 bg-forest px-10 py-5 text-xs font-bold uppercase tracking-[0.25em] text-bone transition-colors hover:bg-moss"
            >
              Shop {page.label} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </EcwidLink>
          </Reveal>
        </div>

        <div className="mx-auto max-w-4xl px-6">
          <Reveal delay={0.05}>
            <h2 className="mt-20 font-serif text-3xl text-ink">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="mt-6">
              {page.faq.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-ink/10">
                  <AccordionTrigger data-testid={`money-faq-${page.slug}-${i}`} className="text-left font-serif text-lg text-ink hover:text-bronze">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-sage">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          <Reveal delay={0.1}>
            <h2 className="mt-16 text-[11px] font-bold uppercase tracking-[0.3em] text-bronze">More from the menu</h2>
            <div className="mt-5 flex flex-wrap gap-2">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/${p.slug}`}
                  data-testid={`money-crosslink-${p.slug}`}
                  className="border border-ink/10 px-4 py-2 text-xs text-sage transition-colors hover:border-gold/50 hover:text-ink"
                >
                  {p.label}
                </Link>
              ))}
            </div>
            <p className="mt-10 text-xs leading-relaxed text-ash">
              {BUSINESS.name} · {BUSINESS.address}, {BUSINESS.city}, {BUSINESS.state} {BUSINESS.zip} · {BUSINESS.phone} · {BUSINESS.hours} · Adults 21+
            </p>
          </Reveal>
        </div>
      </div>
    </>
  );
}
